import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AUTOPAY_ENHANCEMENT, CHANGE_PAYMENT_METHOD_URL } from '@rci/self-serve-fido/app.constant';
import { AnalyticsServiceProxy } from '@rci/self-serve-fido/core/analytics';
import { AuthenticationService } from '@rci/self-serve-fido/shared/authentication/authentication.service';
import { modalWindowConfig } from '@rci/self-serve-fido/shared/modal-window.config';
import { NavigationService } from '@rci/self-serve-fido/shared/navigation-service/navigation-service.service';
import { ViewContainerRefService } from '@rci/self-serve-fido/shared/view-container-ref/view-container-ref.service';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { ApiEndpointService } from '@rci/self-serve-shared';
import { ModalInjectableData } from '@rogers/cdk/common/classes';
import { ModalService } from '@rogers/cdk/modal';
import { AccountListFacadeService, UserAccount } from '@rogers/self-serve-common/account';
import { BillingDetail, PaymentMethodType } from '@rogers/self-serve-common/billing-detail';
import { UserPermissionFacadeService } from '@rogers/self-serve-common/user-permission';
import { AutopayDiscountRow, AutopayDiscountsFacade, AutopayEnrollmentAction } from '@rogers/self-serve-payments';
import { RogersBankOfferFacadeService } from '@rogers/self-serve-rogers-bank';
import { get } from 'lodash';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { AlertId, UniqueAlertId } from '../billing-alerts/state.interface';
import { autopayPromoAnalyticsConfig } from './analytics/autopay-promo-analytics-config';
import { AutopayDiscountTableModalComponent } from './autopay-discount-table-modal/autopay-discount-table-modal.component';
import { AutopayPromoDetailsComponent } from './autopay-promo-details/autopay-promo-details.component';
import { AutopayPromoDetails, AutopayPromoResponse, ELIGIBLE_IND_LIST } from './autopay-promo.model';

@Injectable({
  providedIn: 'root',
})
export class AutopayPromoService {
  private readonly accountDetailService = inject(AccountDetailService);
  private readonly accountListFacadeService = inject(AccountListFacadeService);
  private readonly analyticsServiceProxy = inject(AnalyticsServiceProxy);
  private readonly apiEndpointService = inject(ApiEndpointService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly autopayDiscountsFacade = inject(AutopayDiscountsFacade);
  private readonly http = inject(HttpClient);
  private readonly modalService = inject(ModalService);
  private readonly navigationService = inject(NavigationService);
  private readonly translate = inject(TranslateService);
  private readonly userInformationStateFacade = inject(UserInformationStateFacade);
  private readonly viewContainerRefService = inject(ViewContainerRefService);
  private readonly userPermissionFacadeService = inject(UserPermissionFacadeService);
  private readonly rogersBankOfferFacadeService = inject(RogersBankOfferFacadeService);

  private readonly VALID_MOP_FOR_ELIG_IND: Record<string, PaymentMethodType> = {
    UWP: PaymentMethodType.R,
    XEWP: PaymentMethodType.C,
    XEEP: PaymentMethodType.C,
  };

  private readonly FIDO_AUTOPAY_ALERT_MAP: Record<string, { alertId: AlertId; uniqueAlertId: UniqueAlertId }> = {
    UWP_WL_rmc: { alertId: AlertId.AUTOPAY_PROMO_UWP_WL_RMC, uniqueAlertId: UniqueAlertId.AUTOPAY_PROMO_UWP_WL_RMC },
    UWP_WL_pad: { alertId: AlertId.AUTOPAY_PROMO_UWP_WL_PAD, uniqueAlertId: UniqueAlertId.AUTOPAY_PROMO_UWP_WL_PAD },
    XEWP_WL_rmc: { alertId: AlertId.AUTOPAY_PROMO_XEWP_WL_RMC, uniqueAlertId: UniqueAlertId.AUTOPAY_PROMO_XEWP_WL_RMC },
    XEWP_WL_pad: { alertId: AlertId.AUTOPAY_PROMO_XEWP_WL_PAD, uniqueAlertId: UniqueAlertId.AUTOPAY_PROMO_XEWP_WL_PAD },
    XEEP_WL_rmc: { alertId: AlertId.AUTOPAY_PROMO_XEEP_WL_RMC, uniqueAlertId: UniqueAlertId.AUTOPAY_PROMO_XEEP_WL_RMC },
    XEEP_WL_pad: { alertId: AlertId.AUTOPAY_PROMO_XEEP_WL_PAD, uniqueAlertId: UniqueAlertId.AUTOPAY_PROMO_XEEP_WL_PAD },
  };

  private readonly LEGACY_AUTOPAY_ALERT_INFO = {
    alertId: AlertId.AUTOPAY_PROMO,
    uniqueAlertId: UniqueAlertId.AUTOPAY_PROMO,
  };

  getAutopayAlertInfo$(
    account: UserAccount,
    billingDetail: BillingDetail,
  ): Observable<{ alertId: string; uniqueAlertId: string } | null> {
    const { autoPayPromoEligInd, accountNumber } = account;
    if (
      account.isCancelledAccount ||
      !account.isWirelessOrConsolidatedAccount ||
      this.userInformationStateFacade.isSubscriber ||
      !billingDetail?.paymentMethod ||
      !autoPayPromoEligInd
    ) {
      return of(null);
    }
    return this.userPermissionFacadeService.hasUserPermission$(AUTOPAY_ENHANCEMENT).pipe(
      switchMap(isEnhancementOn => {
        if (!isEnhancementOn) {
          return of(this.getLegacyAutopayAlertInfo(account, billingDetail));
        }
        if (!ELIGIBLE_IND_LIST.includes(autoPayPromoEligInd)) {
          return of(null);
        }
        const { mopType } = billingDetail.paymentMethod;
        const validMop = this.VALID_MOP_FOR_ELIG_IND[autoPayPromoEligInd];
        if (!validMop || mopType !== validMop) {
          return of(null);
        }
        return this.rogersBankOfferFacadeService.getRogersBankOffer$(accountNumber).pipe(
          take(1),
          map(offer => {
            const key = `${autoPayPromoEligInd}_WL_${offer?.isCreditCardHolder ? 'rmc' : 'pad'}`;
            return this.FIDO_AUTOPAY_ALERT_MAP[key];
          }),
        );
      }),
    );
  }

  autopayApplyDiscountAPI$(accountAlias: string) {
    const url = `${this.apiEndpointService.promotionsBaseUrl}accounts/${accountAlias}/promo/autopay`;
    return this.http.post(url, '', {
      headers: this.authenticationService.getDomainServiceHeaders(),
    });
  }

  autopayRemoveDiscountAPI$(accountAlias: string) {
    const url = `${this.apiEndpointService.promotionsBaseUrl}accounts/${accountAlias}/promo/autopay`;
    return this.http.delete(url, {
      headers: this.authenticationService.getDomainServiceHeaders(),
    });
  }

  eligibleAutoPromoAccounts$(): Observable<UserAccount> {
    return this.accountListFacadeService.accountList$.pipe(
      mergeMap(account => account),
      filter(
        account =>
          !!account &&
          !account.isCancelledAccount &&
          account.isWirelessOrConsolidatedAccount &&
          account.autoPayPromoEligInd === 'UWP' &&
          !this.userInformationStateFacade.isSubscriber,
      ),
      mergeMap(account =>
        this.accountDetailService.getBillingDetail$(account.accountNumber).pipe(
          filter(
            billingDetail =>
              !!billingDetail && !billingDetail.hasError && billingDetail.paymentMethod.mopType === PaymentMethodType.R,
          ),
          switchMap(() => of(account)),
        ),
      ),
    );
  }

  getAutopayOfferDetails$(accountNumber: string, operation: string, isReview): Observable<AutopayPromoDetails> {
    const isEnroll = operation === 'enroll';
    return this.accountDetailService.getAccountDetail$(accountNumber).pipe(
      switchMap(({ alias: accountAlias }) => {
        const url = `${this.apiEndpointService.promotionsBaseUrl}accounts/${accountAlias}/promo/autopay?promoGroup=AUTOPAY&lob=WL&op=${operation}`;
        return this.http
          .get<AutopayPromoResponse>(url, {
            headers: this.authenticationService.getDomainServiceHeaders(),
          })
          .pipe(
            map(({ promotions }) => ({
              isEnroll,
              modalHeading: isEnroll ? 'overview.autopay-promo.details.heading' : 'autopay-de-enrollment.header',
              modalBody: isEnroll ? 'overview.autopay-promo.details.body' : 'autopay-de-enrollment.body',
              discountMessage: isEnroll
                ? 'overview.autopay-promo.details.eligible-discount'
                : 'autopay-de-enrollment.current-discount',
              promotions,
            })),
            catchError(error => {
              const message = this.translate.instant('overview.autopay-promo.details.error-heading');
              this.trackError(error, message, isEnroll, isReview);
              return of({
                modalHeading: 'overview.autopay-promo.details.error-heading',
                modalBody: 'overview.autopay-promo.details.error-body',
                promotions: [],
              });
            }),
          );
      }),
    );
  }

  openAutopayOfferModal(accountNumber: string, operation = 'enroll', isReview = false) {
    const isEnroll = operation === 'enroll';

    this.userPermissionFacadeService
      .hasUserPermission$(AUTOPAY_ENHANCEMENT)
      .pipe(
        take(1),
        switchMap(isEnhancementOn => {
          if (isEnhancementOn && !isEnroll) {
            this.navigationService.setAccountNumberAndNavigate(accountNumber, CHANGE_PAYMENT_METHOD_URL);
            return EMPTY;
          }

          return isEnhancementOn && isEnroll
            ? this.openOverviewAutopayDiscountModal$(accountNumber)
            : this.openBauPromoModal$(accountNumber, operation, isReview);
        }),
      )
      .subscribe();
  }

  openAutopayDeEnrollmentModal(isReview) {
    this.trackEvent(
      isReview
        ? autopayPromoAnalyticsConfig.DeEnrollReviewChangeMopCTAConfig
        : autopayPromoAnalyticsConfig.DeEnrollChangeMopCTAConfig,
    );
    this.openAutopayOfferModal(this.accountDetailService.selectedAccountDetail.accountNumber, 'de-enroll', isReview);
  }

  private getLegacyAutopayAlertInfo(
    account: UserAccount,
    billingDetail: BillingDetail,
  ): { alertId: string; uniqueAlertId: string } | null {
    const showAutoPayAlert =
      account.autoPayPromoEligInd === 'UWP' && billingDetail.paymentMethod.mopType === PaymentMethodType.R;
    return showAutoPayAlert ? this.LEGACY_AUTOPAY_ALERT_INFO : null;
  }

  private openOverviewAutopayDiscountModal$(accountNumber: string) {
    this.trackEvent(autopayPromoAnalyticsConfig.LearnMoreCTAConfig);

    return this.getOverviewAutopayDiscountRows$(accountNumber).pipe(
      switchMap(discountRows => {
        if (!discountRows.length) {
          this.navigationService.setAccountNumberAndNavigate(accountNumber, CHANGE_PAYMENT_METHOD_URL);
          return EMPTY;
        }

        return this.getHasRogersMastercard$(accountNumber).pipe(
          map(hasRogersMastercard => ({ discountRows, hasRogersMastercard })),
        );
      }),
      switchMap(({ discountRows, hasRogersMastercard }) => {
        if (!discountRows.length) {
          return EMPTY;
        }

        this.trackEvent(autopayPromoAnalyticsConfig.DiscountsModalConfig);

        const modalRef = this.modalService.open(
          AutopayDiscountTableModalComponent,
          this.viewContainerRefService.viewContainerRef,
          modalWindowConfig,
          new ModalInjectableData({
            discountRows,
            hasRogersMastercard,
          }),
        );

        return modalRef.componentInstance.emitUserAction.pipe(
          tap((url: string) => {
            modalRef.close();
            this.handleAutoPromoModalAction(url, true, accountNumber);
          }),
        );
      }),
      catchError(() => {
        this.navigationService.setAccountNumberAndNavigate(accountNumber, CHANGE_PAYMENT_METHOD_URL);
        return EMPTY;
      }),
    );
  }

  private getHasRogersMastercard$(accountNumber: string): Observable<boolean> {
    if (!accountNumber) {
      return of(false);
    }

    return this.rogersBankOfferFacadeService.getRogersBankOffer$(accountNumber).pipe(
      take(1),
      map(offer => !!offer?.isCreditCardHolder),
    );
  }

  private getOverviewAutopayDiscountRows$(accountNumber: string): Observable<AutopayDiscountRow[]> {
    return this.accountDetailService.getAccountDetail$(accountNumber).pipe(
      take(1),
      switchMap(accountDetail => {
        if (!accountDetail?.accountNumber) {
          return of([]);
        }

        this.autopayDiscountsFacade.loadAutopayDiscountsIfNeeded(
          AutopayEnrollmentAction.ENROLL,
          accountDetail,
          'C', // always 'C' for AutopayEnrollmentAction.ENROLL
        );

        return this.autopayDiscountsFacade.getAutopayDiscounts$(
          accountDetail.accountNumber,
          AutopayEnrollmentAction.ENROLL,
        );
      }),
      take(1),
    );
  }

  private openBauPromoModal$(accountNumber: string, operation: string, isReview: boolean) {
    const isEnroll = operation === 'enroll';
    if (isEnroll) {
      this.trackEvent(autopayPromoAnalyticsConfig.LearnMoreCTAConfig);
    }

    return this.getAutopayOfferDetails$(accountNumber, operation, isReview).pipe(
      take(1),
      map((promotionDetails: AutopayPromoDetails) => {
        this.trackEvent(
          isEnroll ? autopayPromoAnalyticsConfig.DiscountsModalConfig : autopayPromoAnalyticsConfig.DeEnrollModalConfig,
        );

        return this.modalService.open(
          AutopayPromoDetailsComponent,
          this.viewContainerRefService.viewContainerRef,
          modalWindowConfig,
          new ModalInjectableData({
            language: this.translate.currentLang,
            promotionDetails,
          }),
        );
      }),
      switchMap(modalRef =>
        modalRef.componentInstance.emitUserAction.pipe(
          tap((url: string) => {
            modalRef.close();
            this.handleAutoPromoModalAction(url, isEnroll, accountNumber);
          }),
        ),
      ),
    );
  }

  private handleAutoPromoModalAction(url: string, isEnroll: boolean, accountNumber: string) {
    if (url && url !== 'cancel') {
      this.trackEvent(
        isEnroll
          ? autopayPromoAnalyticsConfig.SetupAutopayCTAConfig
          : autopayPromoAnalyticsConfig.DeEnrollKeepAutopayCTA,
      );
      this.navigationService.setAccountNumberAndNavigate(accountNumber, url);
    } else {
      this.trackCloseEvent(isEnroll, url === 'cancel');
    }
  }

  private trackCloseEvent(isEnroll, isCancel) {
    let eventData;
    if (isEnroll) {
      eventData = autopayPromoAnalyticsConfig.CancelCTAConfig;
    } else if (isCancel) {
      eventData = autopayPromoAnalyticsConfig.DeEnrollCancelCTA;
    } else {
      eventData = autopayPromoAnalyticsConfig.DeEnrollContinueCTA;
    }
    this.trackEvent(eventData);
  }

  private trackEvent(eventData) {
    this.analyticsServiceProxy.trackEvent(eventData);
  }

  private trackError(error, errorMessage, isEnroll, isReview) {
    let mappingKey: string;
    if (isEnroll) {
      mappingKey = autopayPromoAnalyticsConfig.ApiFailureConfig.mappingKey;
    } else if (isReview) {
      mappingKey = autopayPromoAnalyticsConfig.DeEnrollReviewChangeMopApiFailureConfig.mappingKey;
    } else {
      mappingKey = autopayPromoAnalyticsConfig.DeEnrollChangeMopApiFailureConfig.mappingKey;
    }

    this.analyticsServiceProxy.trackError(
      {
        category: 'self serve',
        code: get(error.error?.status, 'code'),
        API: error.url?.replace(/customer\/accounts\/(.+)\/promo\/autopay/, 'customer/accounts/promo/autopay'),
        message: '',
        type: error.status === 412 ? 'user' : 'http',
        field: '',
        mappingKey,
      },
      errorMessage,
    );
  }
}
